import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { websiteInvoiceService } from '../services/websiteInvoiceService';

export const PaymentSuccessPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = React.useState('processing');
  const [invoiceId, setInvoiceId] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  
  useEffect(() => {
    const processPaymentSuccess = async () => {
      try {
        // Extract payment intent ID from location state or URL
        const paymentIntentId = location.state?.paymentIntentId;
        
        if (paymentIntentId) {
          // Call the payment success endpoint
          const response = await apiService.post('/api/v1/stripe/b2b/payment-success', {
            payment_intent_id: paymentIntentId
          }) as any;
          
          if (response?.success) {
            setInvoiceId(response?.invoice_id);
            setStatus('success');
          } else {
            setError(response?.error_message || 'Payment processing failed');
            setStatus('error');
          }
        } else {
          setError('No payment information found');
          setStatus('error');
        }
      } catch (err: any) {
        setError(err.message || 'An error occurred while processing your payment');
        setStatus('error');
        console.error('Payment processing error:', err);
      }
    };
    
    processPaymentSuccess();
  }, [location.state]);
  
  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };
  
  const handleViewInvoice = () => {
    if (invoiceId) {
      navigate(`/invoices/${invoiceId}`);
    }
  };
  
  if (status === 'processing') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Processing Payment</h2>
          <p className="text-gray-600">Please wait while we process your payment...</p>
        </div>
      </div>
    );
  }
  
  if (status === 'error') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-red-600 mb-4">Payment Processing Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="space-x-4">
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Return Home
            </button>
            <button
              onClick={() => navigate('/plans')}
              className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center max-w-md">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-green-600 mb-4">Payment Successful!</h2>
        <p className="text-gray-600 mb-6">
          Thank you for your subscription. Your payment has been processed successfully.
        </p>
        <div className="space-x-4">
          <button
            onClick={handleViewInvoice}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            disabled={!invoiceId}
          >
            View Invoice
          </button>
          {invoiceId && (
            <button
              onClick={async () => {
                const success = await websiteInvoiceService.downloadPdf(invoiceId);
                if (!success) {
                  alert('Failed to download PDF. Please try again later.');
                }
              }}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            >
              Download PDF
            </button>
          )}
          <button
            onClick={handleBackToDashboard}
            className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};